import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';

import type { TimetableMatrix } from '../../utils/fixedRoutes/buildTimetableMatrix';
import { secondsToHHMM } from '../../utils/map/timeOfDay';

/**
 * bustimes.org-style timetable grid: stops are rows, journeys are columns, each cell the
 * time the journey calls at that stop. A sticky stop-name column stays put while the time
 * columns scroll horizontally. Pure presentation over a prebuilt `TimetableMatrix` — both
 * the hosted-network and own-route surfaces feed the same component.
 */

export type TimetableGridMode = 'timing' | 'all';

interface TimetableGridProps {
  matrix: TimetableMatrix;
  routeColor?: string;
  /** Stop currently highlighted (e.g. hovered) — mirrored on the companion mini-map. */
  focusedStopId?: string | null;
  onFocusStop?: (stopId: string | null) => void;
  defaultMode?: TimetableGridMode;
  onModeChange?: (mode: TimetableGridMode) => void;
}

/** Format a GTFS seconds value for a cell, flagging after-midnight calls (bustimes' ⁺¹). */
function formatCell(seconds: number | null): { text: string; nextDay: boolean } {
  if (seconds == null) return { text: '', nextDay: false };
  const nextDay = seconds >= 86400;
  return { text: secondsToHHMM(nextDay ? seconds - 86400 : seconds), nextDay };
}

const STOP_COL_WIDTH = 184;

function TimetableGrid({
  matrix,
  routeColor,
  focusedStopId,
  onFocusStop,
  defaultMode = 'timing',
  onModeChange,
}: TimetableGridProps): React.ReactElement {
  const { t } = useTranslation(['mapCore']);
  const [mode, setMode] = useState<TimetableGridMode>(defaultMode);

  // "Timing points" hides untimed intermediate stops (unless the feed has none, in which
  // case the toggle isn't offered and we show everything).
  const showToggle = matrix.hasNonTimingPoints;
  const effectiveMode: TimetableGridMode = showToggle ? mode : 'all';

  const rows = useMemo(
    () =>
      matrix.stops
        .map((stop, index) => ({ stop, index }))
        .filter(({ stop }) => effectiveMode === 'all' || stop.isTimingPoint),
    [matrix.stops, effectiveMode],
  );

  const handleMode = (next: TimetableGridMode) => {
    setMode(next);
    onModeChange?.(next);
  };

  const accent = routeColor || undefined;

  if (matrix.journeys.length === 0 || matrix.stops.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('timetableGrid.empty')}
      </Typography>
    );
  }

  return (
    <Box>
      {showToggle && (
        <RadioGroup
          row
          value={effectiveMode}
          onChange={(e) => handleMode(e.target.value as TimetableGridMode)}
          sx={{ mb: 0.5, gap: 1 }}
        >
          <FormControlLabel
            value="timing"
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{t('timetableGrid.timingPoints')}</Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlaceIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{t('timetableGrid.allStops')}</Typography>
              </Box>
            }
          />
        </RadioGroup>
      )}

      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 420,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontVariantNumeric: 'tabular-nums',
            width: 'max-content',
            minWidth: '100%',
          }}
        >
          <Box component="tbody">
            {rows.map(({ stop, index }) => {
              const isFocused = focusedStopId != null && focusedStopId === stop.stopId;
              return (
                <Box
                  component="tr"
                  key={`${stop.stopId}-${index}`}
                  onMouseEnter={() => onFocusStop?.(stop.stopId)}
                  onFocus={() => onFocusStop?.(stop.stopId)}
                  sx={{ bgcolor: isFocused ? 'action.hover' : 'transparent' }}
                >
                  {/* Sticky stop-name column */}
                  <Box
                    component="th"
                    scope="row"
                    tabIndex={0}
                    onClick={() => onFocusStop?.(stop.stopId)}
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      textAlign: 'left',
                      fontWeight: stop.isTimingPoint ? 600 : 400,
                      fontSize: '0.78rem',
                      color: stop.isTimingPoint ? 'text.primary' : 'text.secondary',
                      bgcolor: isFocused ? 'action.selected' : 'background.paper',
                      borderBottom: 1,
                      borderColor: 'divider',
                      borderLeft: isFocused ? `3px solid ${accent || '#1976d2'}` : '3px solid transparent',
                      px: 1,
                      py: 0.5,
                      width: STOP_COL_WIDTH,
                      minWidth: STOP_COL_WIDTH,
                      maxWidth: STOP_COL_WIDTH,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                    }}
                    title={stop.name}
                  >
                    {stop.name}
                  </Box>

                  {/* One time cell per journey */}
                  {matrix.journeys.map((journey, col) => {
                    const { text, nextDay } = formatCell(matrix.times[index][col]);
                    return (
                      <Box
                        component="td"
                        key={journey.id}
                        sx={{
                          textAlign: 'center',
                          fontSize: '0.78rem',
                          color: text ? 'text.primary' : 'text.disabled',
                          borderBottom: 1,
                          borderColor: 'divider',
                          px: 0.75,
                          py: 0.5,
                          minWidth: 52,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {text}
                        {nextDay && (
                          <Box component="sup" sx={{ fontSize: '0.6rem', ml: 0.1, color: 'text.secondary' }}>
                            +1
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default TimetableGrid;
